import React, { forwardRef } from "react";
import { BUTTON_SIZES } from "@miplatform/constants";
import { toKebabCase } from "@miplatform/utils";
import {
  Button,
  ButtonProps,
  SxProps,
  Theme,
  Tooltip,
  useTheme,
} from "@mui/material";
import { MiIcon } from "../MiIcon/MiIcon";

interface MiButtonIconSpec {
  path: string;
  iconColor?: string;
  iconSize?: number;
  iconStyle?: React.CSSProperties;
}

interface TooltipWrapperProps {
  description?: string;
  children: React.ReactElement;
}

interface MiButtonProps
  extends Omit<ButtonProps, "startIcon" | "endIcon" | "color"> {
  title?: string;
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  variant?: ButtonProps["variant"];
  size?: ButtonProps["size"];
  color?: keyof Theme["palette"] | string;
  disabled?: boolean;
  sx?: SxProps<Theme>;
  disableElevation?: boolean;
  startIcon?: MiButtonIconSpec;
  endIcon?: MiButtonIconSpec;
  description?: string;
  width?: string | number;
  children?: React.ReactNode;
  className?: string;
}

function TooltipWrapper({
  description,
  children,
}: Readonly<TooltipWrapperProps>) {
  return <Tooltip title={description}>{children}</Tooltip>;
}

const renderIcon = (icon?: MiButtonIconSpec): React.ReactNode | null => {
  return icon ? (
    <MiIcon
      path={icon.path}
      size={icon?.iconSize || 0.8}
      color={icon?.iconColor}
      style={icon?.iconStyle}
    />
  ) : null;
};

export const MiButton = forwardRef<HTMLButtonElement, MiButtonProps>(
  (
    {
      title = "",
      onClick,
      variant,
      size = BUTTON_SIZES.MEDIUM as ButtonProps["size"],
      color,
      disabled = false,
      sx = {},
      disableElevation = true,
      startIcon,
      endIcon,
      description = "",
      width,
      children,
      className,
      ...rest
    },
    ref
  ) => {
    const theme = useTheme();

    const button = (
      <Button
        onClick={onClick}
        variant={variant}
        size={size}
        color={color as ButtonProps["color"]}
        disabled={disabled}
        disableElevation={disableElevation}
        startIcon={renderIcon(startIcon)}
        endIcon={renderIcon(endIcon)}
        sx={{
          textTransform: "none",
          minWidth: "max-content",
          borderRadius: "4px",
          display: "flex",
          width,
          gap: title ? theme.spacing(1) : 0,
          "& .MuiButton-startIcon": {
            marginRight: 0,
            marginLeft: 0,
          },
          "& .MuiButton-endIcon": {
            marginRight: 0,
            marginLeft: 0,
          },
          ...sx,
        }}
        data-testid={
          typeof title === "string" ? `${toKebabCase(title)}-btn` : "btn"
        }
        className={className}
        ref={ref}
        {...rest}
      >
        {title}
        {children}
      </Button>
    );

    if (description) {
      return (
        <TooltipWrapper description={description}>{button}</TooltipWrapper>
      );
    }

    return button;
  }
);

MiButton.displayName = "MiButton";
